import { SEO_DEFAULTS } from '../../../../shared/constants/SEODefaults';
import {
  Image,
  HeroSection,
  ImageTitleDescContainer,
  ImageTitleDesc,
  FaqsSection,
  KnowMoreCta,
  SeoWithSchema,
} from '../../types';
import {
  PartnersContentApiResponse,
  ApiProfile,
  ApiTestimonialSection,
  ApiPartnerOwnVehicle,
  ApiImageSrc,
  ApiImageSingle,
  ApiHeroSection,
  ApiImageTitleDescContainer,
  ApiImageTitleDesc,
  ApiFaqsSection,
  ApiLink,
  ApiKnowMoreCta,
  ApiMetaJson,
} from '../api-model/PartnersContentApiModel';
import {
  PartnersContentEntity,
  PartnerProfile,
  TestimonialSection,
  PartnerOwnVehicle,
} from '../entities/PartnersContentEntity';

function getImageFromSrc(model: ApiImageSrc): Image {
  const attrs = model?.data?.attributes;
  return {
    path: attrs?.url ?? '',
    alt_text: attrs?.alternativeText ?? attrs?.name ?? '',
  };
}

function getImageFromSingleImage(model: ApiImageSingle): Image {
  return {
    path: model?.image?.data?.attributes?.url ?? '',
    alt_text: model?.alt_text ?? '',
  };
}

function getKnowMoreCta(model: ApiKnowMoreCta): KnowMoreCta {
  return {
    label: model.label,
    href: model.href ?? '',
  };
}

function getLink(model: ApiLink): {
  href: string;
  label: string;
  target: string;
  is_external: boolean;
} {
  return {
    href: model.href,
    label: model.label,
    target: model.target,
    is_external: model.isExternal,
  };
}

function getHeroSection(model: ApiHeroSection): HeroSection {
  return {
    title: model.title,
    sub_text: model.sub_text,
    know_more_cta: getKnowMoreCta(model.know_more_cta),
    banner_img: getImageFromSingleImage(model.banner_img),
    banner_img_mobile: getImageFromSingleImage(model.banner_img_mobile),
  };
}

function getImageTitleDesc(model: ApiImageTitleDesc): ImageTitleDesc {
  return {
    title: model.title,
    desc: model.desc,
    icon: getImageFromSrc(model.icon),
  };
}

function getImageTitleDescContainer(
  model: ApiImageTitleDescContainer,
): ImageTitleDescContainer {
  return {
    title: model.title,
    cards: model.cards.map(getImageTitleDesc),
  };
}

function getFaqsSection(model: ApiFaqsSection): FaqsSection {
  return {
    title: model.title,
    sub_text: model.sub_text ?? '',
    faqs: model.faqs.map((faq) => ({
      question: faq.question,
      answer: faq.answer,
    })),
    toggle_icon: getImageFromSingleImage(model.toggle_icon),
  };
}

function getMeta(model: ApiMetaJson | undefined): SeoWithSchema {
  if (!model) {
    return {
      title: SEO_DEFAULTS.title,
      description: SEO_DEFAULTS.description,
      schemas: [],
      image: { path: SEO_DEFAULTS.image_src, alt_text: SEO_DEFAULTS.image_alt },
      is_no_index: false,
    };
  }

  const schemas: unknown[] = [
    model.faq_schema,
    model.review_schema,
    model.local_business_schema,
  ].filter(Boolean);

  const imagePath = model.image?.image?.data?.attributes?.url ?? SEO_DEFAULTS.image_src;
  const imageAlt = model.image?.alt_text ?? SEO_DEFAULTS.image_alt;

  return {
    title: model.title ?? SEO_DEFAULTS.title,
    description: model.description ?? SEO_DEFAULTS.description,
    schemas,
    image: { path: imagePath, alt_text: imageAlt },
    is_no_index: model.is_no_index ?? false,
  };
}

function getTestimonialSection(model: ApiTestimonialSection): TestimonialSection {
  return {
    title: model.title,
    profiles: model.profiles.map(
      (profile: ApiProfile): PartnerProfile => ({
        name: profile.name,
        city: profile.city,
        image: getImageFromSrc(profile.image),
        description: profile.description,
      }),
    ),
  };
}

function getPartnerOwnVehicle(model: ApiPartnerOwnVehicle): PartnerOwnVehicle {
  return {
    title: model.title,
    sub_title: model.sub_title,
    action: getLink(model.action),
    image: getImageFromSingleImage(model.image),
  };
}

export function apiModelToEntity(
  response: PartnersContentApiResponse,
): PartnersContentEntity {
  const data = response.data.attributes;
  return {
    hero_section: getHeroSection(data.hero_section),
    porter_advantage: getImageTitleDescContainer(data.porter_advantage),
    query_info: getImageTitleDesc(data.life_easy_section),
    additioanal_benifits: getImageTitleDescContainer(data.additioanal_benifits),
    testimonial_section: getTestimonialSection(data.testimonial_section),
    faq_section: getFaqsSection(data.faq_section),
    download_app: getImageTitleDesc(data.download_app),
    partner_own_vehicle: getPartnerOwnVehicle(data.partner_own_vehicle),
    meta_data: getMeta(data.meta_data),
    get_partner_cta: getKnowMoreCta(data.get_partner_cta),
  };
}
